import type {
  ConfigurationValidationError,
  ValidationErrorMessage,
} from '@festina/configuration-general-types/configuration'
import {
  type BranchKey,
  getValidationErrorKey,
  type LayerKey,
  type LocationErrors,
  OperationLayerMap,
  useFetchBranchErrorsOnAllBlocksAndLayers,
  type WorkspaceKey,
} from '@festina-life/api'
import { notEmpty, type Optional } from '@festina-life/core'
import { useMemo } from 'react'

import { useMetaModelContext } from '../../context/metaModelContext'
import { useWorkspaceErrorsOnAllLayers } from '../workspace/useWorkspaceErrorsOnAllLayers'
import { collectAndCategoriseErrors } from './helpers'
import type { ValidationErrorWithLocation } from './types'

export const useLocationErrors = ({
  enabled,
  workspaceKey,
  branchKey,
  allLayers,
  allBlocks,
}: {
  enabled: boolean
  workspaceKey: Optional<WorkspaceKey>
  branchKey: Optional<BranchKey>
  allLayers: LayerKey[]
  allBlocks: string[]
}): {
  errors: ValidationErrorWithLocation<ConfigurationValidationError>[]
  isLoading: boolean
  workspaceValidationErrors: Map<
    string,
    OperationLayerMap<ValidationErrorMessage | Error>
  >
  branchValidationErrors: Map<
    string,
    OperationLayerMap<ValidationErrorMessage | Error>
  >
} => {
  const { layerTopology, blockTopology } = useMetaModelContext()
  const location = workspaceKey ? 'workspace' : 'baseBranch'
  const { data: workspaceErrors, isFetching: isWorkspaceErrorsFetching } =
    useWorkspaceErrorsOnAllLayers({
      workspaceKey,
      enabled: location === 'workspace' && enabled,
      allLayers,
      allBlocks,
      layerTopology,
      blockTopology,
    })

  const { data: baseBranchErrors, isFetching: isBaseBranchErrorsFetching } =
    useFetchBranchErrorsOnAllBlocksAndLayers({
      enabled,
      server: branchKey?.server,
      branchName: branchKey?.name,
      layers: allLayers,
      blocks: allBlocks,
      layerTopology,
      blockTopology,
    })

  const categorisedErrors = useMemo(() => {
    const allBranchErrorKeys = new Set(
      baseBranchErrors
        ?.filter(notEmpty)
        .flatMap(({ errors }) =>
          errors.map((error) => getValidationErrorKey(error)),
        ),
    )

    // If a workspace exists fetching workspace errors will also fetch branch errors for the workspace. So we filter duplicates here.
    // We still want to perform the fetch in case the branch validation throws a generic validation error that we want to capture inside baseBranchErrorsError
    const workspaceErrorsWithoutBranchErrors =
      workspaceErrors?.map(
        ({ errors, ...rest }) =>
          ({
            ...rest,
            errors: errors.filter(
              (error) => !allBranchErrorKeys.has(getValidationErrorKey(error)),
            ),
          }) satisfies LocationErrors,
      ) ?? []

    return collectAndCategoriseErrors({
      workspaceErrors: workspaceErrorsWithoutBranchErrors,
      branchErrors: baseBranchErrors ?? [],
    })
  }, [baseBranchErrors, workspaceErrors])

  const serverErrorsByBlockAndLayer = (errors: LocationErrors[]) => {
    const map = new Map<
      string,
      OperationLayerMap<ValidationErrorMessage | Error>
    >()

    errors.forEach(({ layer, block, serverError }) => {
      if (serverError) {
        const blockErrors = map.get(block) ?? new OperationLayerMap()

        blockErrors.set(layer, serverError)
        map.set(block, blockErrors)
      }
    })
    return map
  }

  return {
    errors: categorisedErrors,
    isLoading: isBaseBranchErrorsFetching || isWorkspaceErrorsFetching,
    workspaceValidationErrors: serverErrorsByBlockAndLayer(
      workspaceErrors ?? [],
    ),
    branchValidationErrors: serverErrorsByBlockAndLayer(baseBranchErrors ?? []),
  }
}
